import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@clerk/clerk-react";
import { Loader2, AlertCircle } from "lucide-react";
import {
  ReactFlow,
  Controls,
  ConnectionMode,
  NodeResizer,
  Panel,
  useViewport,
  useReactFlow,
  type NodeProps,
  type NodeTypes,
  type EdgeTypes,
  type Connection,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useCanvasStore, type KFNode, type KFNodeData, type CanvasShape, SHAPE_PREFIX, LINE_SHAPE_KINDS } from "@/store/useCanvasStore";
import { useViewportCtx } from "@/contexts/ViewportCtx";
import NodeCard, { type NodeType } from "./NodeCard";
import { CV } from "@/config/canvasSizing";
import { CURSORS } from "@/config/cursors";
import { THEME } from "@/config/theme";
import AnimatedEdge from "./AnimatedEdge";
import { EdgeMarkers, EDGE_MARKER_ID } from "./EdgeMarkers";
import { NODE_HANDLE_STYLE, NODE_LINE_STYLE } from "./nodeHandleStyle";
import { computeRevealedSet } from "./revealSelection";
import RevealCamera from "./RevealCamera";
import HandFocusCamera from "./HandFocusCamera";
import ShapeNode from "./ShapeNode";
import Toolbar from "./Toolbar";
import PropertiesPanel from "./PropertiesPanel";
import PencilOptions from "./PencilOptions";
import DrawingLayer from "./DrawingLayer";
import NodePageDrawer from "./NodePageDrawer";
import CanvasAtmosphere from "./CanvasAtmosphere";
import StatusPill from "./StatusPill";
import FocusModeEditor from "./FocusModeEditor";
import RevealHUD from "./RevealHUD";
import RevealEditBar from "./RevealEditBar";
import ConnectableHandles from "./ConnectableHandles";
import CanvasGuide from "./CanvasGuide";
import MeasureHealer from "./MeasureHealer";
import PanelCamera from "./PanelCamera";
import CanvasPlaybar from "./CanvasPlaybar";
import { useMobileSelection, type MobileSelectionCtx } from "./useMobileSelection";
import { useNodePageStore } from "@/store/useNodePageStore";

// ── RF node wrapper ───────────────────────────────────────────────────────────


const SPRING = { type: "spring", mass: 0.4, stiffness: 140, damping: 15 } as const;
const INSTANT = { duration: 0 } as const;

// Self-assembly entrance: on canvas load (and rebuild), each node fades + pops in
// with a stable per-node stagger derived from its id, so the map assembles itself
// with a gentle cascade. Purely a mount animation — decoupled from reveal-step
// logic and skipped in reveal mode so the progressive-disclosure spring (which
// relies on the inner motion.div's initial={false}) is untouched.
const ASSEMBLE_TRANSITION = { duration: 0.4, ease: "easeOut" } as const;
function assemblyDelay(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return (Math.abs(h) % 8) * 0.05; // 0–0.35s
}

function KFNodeCard({ data, selected }: NodeProps<KFNode>) {
  const d = data as KFNodeData;
  const resizeNode           = useCanvasStore((s) => s.resizeNode);
  const updateNode           = useCanvasStore((s) => s.updateNode);
  const activeTool           = useCanvasStore((s) => s.activeTool);
  const isRevealMode         = useCanvasStore((s) => s.isRevealMode);
  const isRevealEditMode     = useCanvasStore((s) => s.isRevealEditMode);
  const revealHighlightId    = useCanvasStore((s) => s.revealHighlightId);
  const setRevealHighlightId = useCanvasStore((s) => s.setRevealHighlightId);
  const focusNodeId          = useCanvasStore((s) => s.focusNodeId);
  const focusNode            = useCanvasStore((s) => s.focusNode);
  const isFocused            = focusNodeId === d.id;

  // Hand (pan) tool: a plain click focuses this node — yellow highlight + badge
  // pulse + camera glide (HandFocusCamera). Handled here on the node's own DOM
  // element (not RF onNodeClick) because elementsSelectable is off in hand mode.
  // A drag still pans (no click fires); stopPropagation keeps the pane handler
  // from immediately clearing the focus we just set.
  const handleFocusClick = useCallback((e: React.MouseEvent) => {
    if (activeTool !== "hand") return;
    e.stopPropagation();
    focusNode(d.id);
  }, [activeTool, focusNode, d.id]);

  const [isEditingLabel, setIsEditingLabel] = useState(false);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (activeTool !== "arrow") return;
    e.stopPropagation();
    setIsEditingLabel(true);
  }, [activeTool]);

  const handleLabelCommit = useCallback((newLabel: string) => {
    setIsEditingLabel(false);
    if (newLabel !== d.label) updateNode(d.id, { label: newLabel });
  }, [d.id, d.label, updateNode]);
  const revealVisible = isRevealMode ? !!(d as unknown as Record<string, unknown>).revealVisible : true;
  // Left undefined until the pages actually load — NodeCard then falls back to
  // the count that came inline on the canvas payload.
  const pages = useNodePageStore((s) => s.pagesByNode[d.id]);
  const prefetching = useNodePageStore((s) => s.prefetching);

  // The badge opens this node's pages panel; PanelCamera (inside ReactFlow)
  // watches the open node and flies the camera to it per the active treatment.
  // The old in-node dropdown is superseded by the panel (mock parity).
  const handleBadgeClick = useCallback(() => {
    useNodePageStore.getState().openDrawer(d.id, d.label);
  }, [d.id, d.label]);

  return (
    <motion.div
      className="w-full h-full group"
      // Self-assembly entrance (non-reveal only — see ASSEMBLE_TRANSITION note).
      // `tt-pop` (design mock): scale overshoots .6 → 1.06 → 1 for a springy pop.
      initial={isRevealMode ? false : { opacity: 0, scale: 0.6 }}
      animate={isRevealMode ? { opacity: 1, scale: 1 } : { opacity: 1, scale: [0.6, 1.06, 1] }}
      transition={isRevealMode ? INSTANT : {
        ...ASSEMBLE_TRANSITION,
        delay: assemblyDelay(d.id),
        scale: { duration: 0.6, times: [0, 0.6, 1], ease: "easeOut", delay: assemblyDelay(d.id) },
      }}
      onMouseEnter={() => isRevealEditMode && setRevealHighlightId(d.id)}
      onMouseLeave={() => isRevealEditMode && setRevealHighlightId(null)}
      onClick={handleFocusClick}
      onDoubleClick={handleDoubleClick}
    >
      <NodeResizer
        isVisible={selected && activeTool !== "hand"}
        minWidth={CV.node.minW}
        minHeight={CV.node.minH}
        handleStyle={NODE_HANDLE_STYLE}
        lineStyle={NODE_LINE_STYLE}
        onResizeEnd={(_event, params) => {
          resizeNode(d.id, params.x, params.y, params.width, params.height);
        }}
      />
      <ConnectableHandles />
      <motion.div
        className="w-full h-full"
        initial={false}
        animate={revealVisible ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 12, scale: 0.92 }}
        transition={revealVisible && isRevealMode ? SPRING : INSTANT}
      >
        <NodeCard
          type={d.type as NodeType}
          label={d.label}
          icon={d.icon}
          color={d.color}
          selected={selected}
          focused={isFocused}
          enlightened={revealVisible}
          revealHighlight={(isRevealEditMode && revealHighlightId === d.id) || isFocused}
          hasDrillTarget={!!d.drillForgeId}
          pages={pages}
          pageCount={d.pageCount}
          prefetching={prefetching}
          isEditingLabel={isEditingLabel}
          onBadgeClick={handleBadgeClick}
          onLabelCommit={handleLabelCommit}
        />
      </motion.div>
    </motion.div>
  );
}

function KFShapeNode({ data, selected }: NodeProps<KFNode>) {
  const d = data as unknown as CanvasShape;
  const moveShape            = useCanvasStore((s) => s.moveShape);
  const resizeShape          = useCanvasStore((s) => s.resizeShape);
  const updateShapeEndpoint  = useCanvasStore((s) => s.updateShapeEndpoint);
  const updateShape          = useCanvasStore((s) => s.updateShape);
  const activeTool           = useCanvasStore((s) => s.activeTool);
  const isRevealMode         = useCanvasStore((s) => s.isRevealMode);
  const isRevealEditMode     = useCanvasStore((s) => s.isRevealEditMode);
  const revealHighlightId    = useCanvasStore((s) => s.revealHighlightId);
  const setRevealHighlightId = useCanvasStore((s) => s.setRevealHighlightId);
  const { screenToFlowPosition } = useReactFlow();

  const rfId = `${SHAPE_PREFIX}${d.id}`;
  const revealHighlight = isRevealEditMode && revealHighlightId === rfId;
  const revealVisible = isRevealMode ? !!(data as unknown as Record<string, unknown>).revealVisible : true;
  const isLine = LINE_SHAPE_KINDS.has(d.kind);
  const isDrawing = d.kind === "drawing";

  const handleResizeEnd = useCallback((_event: unknown, params: { x: number; y: number; width: number; height: number }) => {
    resizeShape(d.id, params.width, params.height);
    moveShape(d.id, params.x, params.y);
  }, [d.id, resizeShape, moveShape]);

  const handleHoverChange = useCallback((hovering: boolean) => {
    if (!isRevealEditMode) return;
    setRevealHighlightId(hovering ? rfId : null);
  }, [isRevealEditMode, rfId, setRevealHighlightId]);

  const handleEndpointMove = useCallback((which: "a" | "b", x: number, y: number) => {
    updateShapeEndpoint(d.id, which, x, y);
  }, [d.id, updateShapeEndpoint]);

  const handleContentCommit = useCallback((content: string) => {
    updateShape(d.id, { content });
  }, [d.id, updateShape]);

  return (
    <>
      <ConnectableHandles />
      <NodeResizer
        isVisible={selected && !isLine && !isDrawing}
        minWidth={CV.shape.minW}
        minHeight={CV.shape.minH}
        handleStyle={NODE_HANDLE_STYLE}
        lineStyle={NODE_LINE_STYLE}
        onResizeEnd={handleResizeEnd}
      />
      <ShapeNode
        shape={d}
        selected={selected}
        revealHighlight={revealHighlight}
        revealVisible={revealVisible}
        isRevealMode={isRevealMode}
        editableOnDoubleClick={activeTool === "arrow"}
        screenToFlowPosition={screenToFlowPosition}
        onEndpointMove={handleEndpointMove}
        onContentCommit={handleContentCommit}
        onHoverChange={handleHoverChange}
      />
    </>
  );
}

const nodeTypes: NodeTypes = { kfNode: KFNodeCard, kfShape: KFShapeNode };
const edgeTypes: EdgeTypes = { kfEdge: AnimatedEdge };

const defaultEdgeOptions = {
  markerEnd: EDGE_MARKER_ID,
  style: { stroke: THEME.raw.edgeStroke, strokeWidth: THEME.raw.edgeStrokeWidth },
  labelStyle: { fill: THEME.raw.edgeStroke, fontSize: 11, fontFamily: "Inter, sans-serif" },
  labelBgStyle: { fill: "transparent" },
  // Reconnection is handled by our own endpoint handles (see AnimatedEdge), which
  // also support detaching an edge into a free arrow. RF's native reconnection is
  // disabled (edgesReconnectable=false) so the two systems don't fight.
  reconnectable: false,
};

const connectionLineStyle: React.CSSProperties = {
  stroke: THEME.raw.edgeConnection,
  strokeWidth: THEME.raw.edgeStrokeWidth,
  strokeDasharray: "5 4",
};

// ── Context menu ──────────────────────────────────────────────────────────────

interface CtxMenu {
  x: number;
  y: number;
  type: "node" | "edge" | "shape";
  id: string;
}

// ── Zoom display ─────────────────────────────────────────────────────────────

function ZoomDisplay() {
  const { zoom } = useViewport();
  return (
    <span className="text-[11px] font-mono text-canvas-muted select-none pointer-events-none">
      {Math.round(zoom * 100)}%
    </span>
  );
}

// ── Viewport ──────────────────────────────────────────────────────────────────

export default function Viewport({ isMobile = false }: { isMobile?: boolean }) {
  const { rfNodes, rfEdges, rfShapes, addEdge, activeTool, selectedIds, isLoading, error, forgeId, loadCanvas, isRevealMode, isRevealEditMode, revealPointer, enterRevealMode, exitRevealMode, advanceReveal, retreatReveal, exitRevealEditMode, focusNodeId, focusTick, clearFocus } = useCanvasStore();
  const { setForgeId } = useViewportCtx();
  const { getToken } = useAuth();
  const [ctxMenu, setCtxMenu] = useState<CtxMenu | null>(null);
  // Present mode: the node the user clicked to zoom into (null = framing the
  // whole revealed group). Reset on every step so advancing re-frames the group.
  const [revealFocusId, setRevealFocusId] = useState<string | null>(null);

  // Draggable reveal edit bar
  const [editBarPos, setEditBarPos] = useState(() => ({
    x: Math.round(window.innerWidth / 2) + 220,
    y: 12,
  }));
  const dragOrigin = useRef<{ mx: number; my: number; bx: number; by: number } | null>(null);

  const onEditBarDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragOrigin.current = { mx: e.clientX, my: e.clientY, bx: editBarPos.x, by: editBarPos.y };
    function onMove(ev: MouseEvent) {
      if (!dragOrigin.current) return;
      setEditBarPos({
        x: dragOrigin.current.bx + ev.clientX - dragOrigin.current.mx,
        y: dragOrigin.current.by + ev.clientY - dragOrigin.current.my,
      });
    }
    function onUp() {
      dragOrigin.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [editBarPos]);

  const drawerNodeId    = useNodePageStore((s) => s.nodeId);
  const drawerNodeLabel = useNodePageStore((s) => s.nodeLabel);
  const initialPageId   = useNodePageStore((s) => s.initialPageId);
  const closeDrawer     = useNodePageStore((s) => s.closeDrawer);
  const panelTreatment  = useNodePageStore((s) => s.treatment);

  const isHand = activeTool === "hand";
  const isDrawingTool = activeTool === "pencil" || activeTool === "eraser";
  // Pane cursor under the DrawingLayer overlay — matches the tool so there's no
  // crosshair flash at the pane edges before the overlay takes over.
  const drawingCursor = activeTool === "eraser" ? CURSORS.eraser : CURSORS.pen;

  // Keyboard: R = reveal, Space/→ = advance, ← = retreat, Esc = exit
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "r" || e.key === "R") {
        if (!isRevealMode) enterRevealMode();
        return;
      }
      if (isRevealMode) {
        if (e.key === " " || e.key === "ArrowRight") { e.preventDefault(); advanceReveal(); }
        if (e.key === "ArrowLeft") retreatReveal();
        if (e.key === "Escape") exitRevealMode();
      }
      if (isRevealEditMode && e.key === "Escape") exitRevealEditMode();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isRevealMode, isRevealEditMode, enterRevealMode, exitRevealMode, advanceReveal, retreatReveal, exitRevealEditMode]);

  // Reveal selection — whole group per step (no drip). Each node/shape carries a
  // revealStep; `computeRevealedSet` lights every item whose step has been reached
  // (revealStep <= revealPointer) all at once. Drives node/edge visibility below
  // and the camera fit (RevealCamera).
  const revealItems = useMemo(
    () => [...rfNodes, ...rfShapes].map(n => ({ id: n.id, revealStep: (n.data as KFNodeData).revealStep as number })),
    [rfNodes, rfShapes],
  );
  const revealedSet = useMemo(
    () => (isRevealMode ? computeRevealedSet(revealItems, revealPointer) : new Set<string>()),
    [isRevealMode, revealItems, revealPointer],
  );

  // Reveal — nodes and shapes.
  // NOTE: Never set `transform` on node style — ReactFlow uses transform:translate for positioning.
  // Opacity is handled by motion.div inside each node component; wrapper only controls pointer-events.
  const revealNodes = useMemo((): KFNode[] => {
    const all = [...rfShapes, ...rfNodes];
    if (!isRevealMode) return all;
    return all.map(n => {
      const step = (n.data as KFNodeData).revealStep as number;
      const visible = step > 0 && revealedSet.has(n.id);
      return {
        ...n,
        data: { ...n.data, revealVisible: visible },
        style: {
          ...n.style,
          pointerEvents: visible ? "auto" : "none",
        } as React.CSSProperties,
      };
    });
  }, [isRevealMode, revealedSet, rfShapes, rfNodes]);

  // Reveal opacity — edges appear when their target node is revealed.
  const revealEdges = useMemo(() => {
    if (!isRevealMode) return rfEdges;
    return rfEdges.map(e => {
      const visible = revealedSet.has(e.source) && revealedSet.has(e.target);
      return {
        ...e,
        // revealVisible drives AnimatedEdge's draw-in dash (line grows in on reveal).
        data: { ...e.data, revealVisible: visible } as typeof e.data,
        style: { ...e.style, opacity: visible ? 1 : 0, transition: "opacity 200ms ease" },
        animated: false,
      };
    });
  }, [isRevealMode, revealedSet, rfEdges]);

  // Stable id list for the reveal camera (group fit target).
  const revealedIds = useMemo(() => Array.from(revealedSet), [revealedSet]);

  // Advancing/retreating a step re-frames the whole group: drop any single-node
  // focus and close the pages drawer for a clean present flow (mock resets the
  // active node on advance). Node clicks don't change revealPointer, so opening
  // pages via a click is preserved.
  useEffect(() => {
    if (!isRevealMode) return;
    setRevealFocusId(null);
    useNodePageStore.getState().closeDrawer();
  }, [revealPointer, isRevealMode]);

  const closeMenu = useCallback(() => setCtxMenu(null), []);

  const onConnect = useCallback(
    (conn: Connection) => {
      if (conn.source && conn.target) addEdge(conn.source, conn.target);
    },
    [addEdge],
  );

  const ctxRfId = ctxMenu
    ? ctxMenu.type === "shape" ? `${SHAPE_PREFIX}${ctxMenu.id}` : ctxMenu.id
    : null;
  const isMultiDelete = !!ctxRfId && selectedIds.size > 1 && selectedIds.has(ctxRfId);
  const deleteLabel = isMultiDelete ? `Delete ${selectedIds.size} items` : "Delete";

  const handleDeleteCtx = useCallback(() => {
    if (!ctxMenu) return;
    const { type, id } = ctxMenu;
    const state = useCanvasStore.getState();
    const rfId = type === "shape" ? `${SHAPE_PREFIX}${id}` : id;

    if (state.selectedIds.size > 1 && state.selectedIds.has(rfId)) {
      const { rfNodes: ns, rfEdges: es, rfShapes: ss, selectedIds: sel } = state;
      state.deleteElements({
        nodeIds:  ns.filter(n => sel.has(n.id)).map(n => n.id),
        edgeIds:  es.filter(e => sel.has(e.id)).map(e => e.id),
        shapeIds: ss.filter(n => sel.has(n.id)).map(n => n.id.slice(SHAPE_PREFIX.length)),
      });
    } else {
      state.deleteElements({
        nodeIds:  type === "node"  ? [id] : [],
        edgeIds:  type === "edge"  ? [id] : [],
        shapeIds: type === "shape" ? [id] : [],
      });
    }
    setCtxMenu(null);
  }, [ctxMenu]);

  // Native contextmenu handler on wrapper — more reliable than RF's onNodeContextMenu
  // in some browsers. Uses RF's data-id attribute on .react-flow__node / .react-flow__edge.
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const target = e.target as Element;

    const nodeEl = target.closest(".react-flow__node");
    if (nodeEl) {
      const id = nodeEl.getAttribute("data-id") ?? "";
      if (!id) return;
      const isShape = id.startsWith(SHAPE_PREFIX);
      setCtxMenu({
        x: e.clientX,
        y: e.clientY,
        type: isShape ? "shape" : "node",
        id: isShape ? id.slice(SHAPE_PREFIX.length) : id,
      });
      return;
    }

    const edgeEl = target.closest(".react-flow__edge");
    if (edgeEl) {
      const id = edgeEl.getAttribute("data-id") ?? "";
      if (!id) return;
      setCtxMenu({ x: e.clientX, y: e.clientY, type: "edge", id });
      return;
    }

    closeMenu();
  }, [closeMenu]);

  // ── Mobile touch selection (additive tap) + long-press context menu ──────────
  const openCtxMenu = useCallback((x: number, y: number, ctx: MobileSelectionCtx) => {
    setCtxMenu({ x, y, type: ctx.type, id: ctx.id });
  }, []);
  const mobileSel = useMobileSelection(isMobile && !isDrawingTool, openCtxMenu);

  const handlePaneClick = useCallback(() => {
    closeMenu();
    // Hand tool: clicking empty canvas dismisses the click-to-focus highlight.
    clearFocus();
    // In present mode a click on empty canvas advances the walkthrough. On desktop
    // a node click instead zooms/opens pages (onNodeClick, 3b); on mobile a node
    // tap also advances (no Space key) — so a tap anywhere walks the reveal.
    if (isRevealMode) { advanceReveal(); return; }
    if (isMobile) mobileSel.clearSelectionOnPane();
  }, [closeMenu, clearFocus, isRevealMode, advanceReveal, isMobile, mobileSel]);

  const handleWrapperClick = useCallback(() => {
    if (isMobile && mobileSel.shouldSwallowClick()) return;
    closeMenu();
  }, [isMobile, mobileSel, closeMenu]);

  const rfVars = {
    // Transparent so the CanvasAtmosphere layers (gradient wash / dot grid /
    // bloom) show through from behind ReactFlow.
    "--xy-background-color":                       "transparent",
    "--xy-controls-button-background-color":       THEME.raw.controlsBg,
    "--xy-controls-button-background-color-hover": THEME.raw.controlsBgHover,
    "--xy-controls-button-color":                  THEME.raw.controlsText,
    "--xy-controls-button-color-hover":            THEME.raw.controlsTextHover,
    "--xy-controls-border-color":                  THEME.raw.controlsBorder,
    "--xy-edge-stroke":                            THEME.raw.edgeStroke,
    "--xy-edge-stroke-selected":                   THEME.raw.edgeStrokeSelected,
    "--xy-edge-stroke-width":                      THEME.raw.edgeStrokeWidth,
    "--xy-handle-background-color":                THEME.raw.handleBg,
    "--xy-handle-border-color":                    THEME.raw.handleBorder,
  } as React.CSSProperties;

  return (
    <div className="w-full h-full flex overflow-hidden" style={{ touchAction: "none" }}>
    {/* Touch handlers use the capture phase: ReactFlow's node drag (d3-drag)
        stopPropagation()s touch events on nodes, so bubble-phase handlers never
        fire on the very elements we want to long-press. Capture fires first. */}
    <div
      className={`flex-1 h-full relative${!isHand && !isMobile && !isDrawingTool ? " rf-arrow-tool" : ""}`}
      style={rfVars}
      onClick={handleWrapperClick}
      onContextMenu={isMobile ? undefined : handleContextMenu}
      onTouchStartCapture={isMobile ? mobileSel.onTouchStart : undefined}
      onTouchMoveCapture={isMobile ? mobileSel.onTouchMove : undefined}
      onTouchEndCapture={isMobile ? mobileSel.clearLongPress : undefined}
      onTouchCancelCapture={isMobile ? mobileSel.clearLongPress : undefined}
    >
      <CanvasAtmosphere />
      <EdgeMarkers />
      <ReactFlow
        colorMode={THEME.canvas.colorMode}
        nodes={revealNodes}
        edges={revealEdges}
        onNodesChange={mobileSel.handleNodesChange}
        onEdgesChange={mobileSel.handleEdgesChange}
        onConnect={onConnect}
        edgesReconnectable={false}
        connectionLineStyle={connectionLineStyle}
        connectionMode={ConnectionMode.Loose}
        connectionRadius={CV.edge.connectionRadius}
        deleteKeyCode="Delete"
        onNodeDragStop={(_, node) => {
          if (node.id.startsWith(SHAPE_PREFIX)) {
            useCanvasStore.getState().moveShape(
              node.id.slice(SHAPE_PREFIX.length),
              node.position.x,
              node.position.y,
            );
          } else {
            useCanvasStore.getState().moveNode(node.id, node.position.x, node.position.y);
          }
        }}
        onNodeClick={(_, node) => {
          // Hand (pan) tool click-to-focus is handled on the node's own DOM
          // wrapper (KFNodeCard) so it fires even though elementsSelectable is
          // off in hand mode — RF's onNodeClick isn't reliable then.
          if (isMobile) {
            // Mobile has no Space key — a tap anywhere (node or empty canvas, see
            // handlePaneClick) advances the walkthrough, mirroring desktop's
            // click-to-advance. Outside reveal, taps select as usual.
            if (isRevealMode) { advanceReveal(); return; }
            mobileSel.toggleSelect(node.id);
            return;
          }
          // Desktop present mode: clicking a node zooms in close (RevealCamera)
          // and opens its pages. Shapes have no pages — skip them.
          if (isRevealMode && !node.id.startsWith(SHAPE_PREFIX)) {
            setRevealFocusId(node.id);
            useNodePageStore.getState().openDrawer(node.id, (node.data as KFNodeData).label);
          }
        }}
        onEdgeClick={isMobile ? (_, edge) => {
          if (isRevealMode) { advanceReveal(); return; }
          mobileSel.toggleSelect(edge.id);
        } : undefined}
        onNodeDoubleClick={(_, node) => {
          const d = node.data as KFNodeData;
          if (d.drillForgeId) setForgeId(d.drillForgeId);
        }}
        onSelectionChange={({ nodes, edges }) => {
          useCanvasStore.getState().setSelectedIds(
            new Set([...nodes.map(n => n.id), ...edges.map(e => e.id)]),
          );
        }}
        onPaneClick={handlePaneClick}
        // Panning/zooming the canvas dismisses the context (delete) menu — its
        // position is screen-fixed and would otherwise float over the moving canvas.
        onMoveStart={closeMenu}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        // Full pan-on-scroll (desktop): a scroll gesture slides the canvas on both
        // axes — a vertical wheel/trackpad scroll moves up/down, a horizontal
        // (tilt-wheel or sideways trackpad) scroll moves left/right. Zoom shifts to
        // ctrl+scroll and pinch (RF's zoomActivationKey). Mobile keeps touch-drag pan.
        panOnScroll={!isMobile}
        panOnDrag={isMobile ? true : (isHand ? true : (isDrawingTool ? false : [1, 2]))}
        selectionOnDrag={!isHand && !isMobile && !isDrawingTool}
        selectNodesOnDrag={isMobile ? false : undefined}
        nodesDraggable={!isHand && !isDrawingTool}
        nodesConnectable={!isMobile && !isDrawingTool}
        elementsSelectable={!isDrawingTool && !isHand}
        // Transparent background: the CanvasAtmosphere layers behind ReactFlow
        // (gradient wash + faint white dot grid + indigo bloom) provide the
        // backdrop, replacing the old flat fill + RF dot Background.
        style={{ cursor: isHand ? "grab" : (isDrawingTool ? drawingCursor : "default"), background: "transparent", "--xy-background-color-default": "transparent" } as React.CSSProperties}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={CV.zoom.min}
        maxZoom={CV.zoom.max}
      >
        <Controls
          style={
            isMobile
              ? { marginBottom: `calc(env(safe-area-inset-bottom, 0px) + ${CV.controls.mobileBottomLift}px)` }
              : undefined
          }
        />
        <Panel
          position="bottom-left"
          style={{
            marginLeft: "60px",
            marginBottom: isMobile
              ? `calc(env(safe-area-inset-bottom, 0px) + ${CV.controls.mobileBottomLift}px)`
              : "8px",
          }}
        >
          <ZoomDisplay />
        </Panel>
        {!isMobile && (
          <Panel position="top-right">
            <StatusPill />
          </Panel>
        )}
        {/* Mobile only: the tool bar rides inside the canvas as a top-center Panel,
            capped to a fixed narrow width (so it stays compact even in landscape)
            but never wider than the pane minus a safety gap. The pane already
            excludes the rail, so this also keeps the bar clear of the rail and the
            screen edge. Desktop mounts a draggable floating bar in AppShell. */}
        {isMobile && (
          <Panel
            position="top-center"
            style={{ maxWidth: `min(calc(100% - ${2 * CV.toolbar.mobileInset}px), ${CV.toolbar.mobileMaxWidth}px)` }}
          >
            <Toolbar isMobile />
          </Panel>
        )}
        {isRevealMode && (
          <Panel
            position="bottom-center"
            // Lift above the mobile browser chrome / home indicator, matching the
            // zoom controls — otherwise the HUD hides behind the bottom bar.
            style={isMobile
              ? { marginBottom: `calc(env(safe-area-inset-bottom, 0px) + ${CV.controls.mobileBottomLift}px)` }
              : undefined}
          >
            <RevealHUD />
          </Panel>
        )}
        {/* Camera driver — inside ReactFlow so it can call useReactFlow(). */}
        {isRevealMode && (
          <RevealCamera
            isRevealMode={isRevealMode}
            revealPointer={revealPointer}
            revealedIds={revealedIds}
            focusNodeId={revealFocusId}
          />
        )}
        {/* Pages-panel camera: flies to the open node per treatment (non-reveal;
            reveal has its own camera). */}
        {!isRevealMode && <PanelCamera nodeId={drawerNodeId} treatment={panelTreatment} />}
        {/* Hand-tool click-to-focus camera: zooms to centre the clicked node
            (non-reveal; no drawer). */}
        {!isRevealMode && <HandFocusCamera focusNodeId={focusNodeId} focusTick={focusTick} />}
        {/* Bottom playback bar — desktop, hidden during the walk-through (the
            reveal HUD takes over). */}
        {!isMobile && !isRevealMode && (
          <Panel position="bottom-center">
            <CanvasPlaybar />
          </Panel>
        )}
        {isDrawingTool && <DrawingLayer />}
        {/* Measures WS-added nodes/shapes that a hidden tab's suspended
            ResizeObserver never reported, so their edges can mount. */}
        <MeasureHealer />
      </ReactFlow>

      <PropertiesPanel />
      <PencilOptions />

      {/* Onboarding empty-state — adapts to the workspace→subject→forge funnel */}
      {!isRevealMode && <CanvasGuide isMobile={isMobile} />}

      {isLoading && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-canvas-bg/70 pointer-events-none">
          <Loader2 size={28} className="text-canvas-accent animate-spin" />
        </div>
      )}

      {error && !isLoading && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-red-950/90 border border-red-500/40 text-red-300 text-xs rounded-lg px-3 py-2 shadow-xl max-w-xs">
          <AlertCircle size={14} className="shrink-0" />
          <span className="flex-1 break-words">{error}</span>
          {forgeId && (
            <button
              className="ml-2 text-red-400 hover:text-red-200 underline whitespace-nowrap"
              onClick={() => void loadCanvas(forgeId, getToken)}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {ctxMenu && (
        <div
          data-testid="canvas-ctx-menu"
          className="fixed z-50 bg-zinc-900 border border-white/10 rounded-lg shadow-xl shadow-black/40 py-1 min-w-[120px]"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            data-testid="ctx-menu-delete"
            className="w-full px-3 py-1.5 text-left text-xs text-red-400 hover:bg-zinc-800 transition-colors rounded-md"
            onClick={handleDeleteCtx}
          >
            {deleteLabel}
          </button>
        </div>
      )}

      {/* Node pages panel — floating overlay over the canvas, positioned per the
          active treatment (drawer / panel / glass). It renders its own absolute
          wrapper, so it no longer reflows the canvas as the old side column did. */}
      {drawerNodeId && (
        <NodePageDrawer
          nodeId={drawerNodeId}
          nodeLabel={drawerNodeLabel}
          initialPageId={initialPageId}
          onClose={closeDrawer}
        />
      )}
    </div>

      {/* Focus mode — full-screen overlay, z-50, above canvas + drawer */}
      <FocusModeEditor />

      {/* Reveal edit bar — draggable fixed overlay, independent of toolbar */}
      {isRevealEditMode && (
        <div
          className="fixed z-30 select-none"
          style={{ left: editBarPos.x, top: editBarPos.y }}
        >
          <RevealEditBar
            onClose={exitRevealEditMode}
            onDragHandleMouseDown={onEditBarDragStart}
          />
        </div>
      )}
    </div>
  );
}
